import { TopLineBanner } from '@kk/ui/components/Notifications/TopLineBanner';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { useIsMutating } from '@tanstack/react-query';
import { cn } from 'cn';
import NProgress from 'nprogress';
import { Suspense, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
  RouterProvider,
  redirect,
  ScrollRestoration,
  useLocation,
  useNavigation,
  useRevalidator,
  useRouteError,
} from 'react-router-dom';
import { appInsights } from '@/app-insights';
import { ErrorBoundary } from '@/components/ErrorBoundary/ErrorBoundary';
import Footer from '@/components/Footer/Footer';
import { Header } from '@/components/Header/Header';
import SideNavigation from '@/components/SideNavigation/SideNavigation';
import routes from '@/routes';
import NotFoundPage from '@/routes/NotFoundPage';
import PageSpinner from '@/routes/PageSpinner';
import { GetAuthenticationStatus } from './auth';
import LoginConnectUserPage from './routes/LoginConnectUserPage';
import LoginPage from './routes/LoginPage';
import ReceiptPage from './routes/Receipt';
import ReceiptDeliveryNotFoundPage from './routes/ReceiptDeliveryNotFound';
import ReceiptNewAgreement from './routes/ReceiptNewAgreement';
import SummaryNewAgreement from './routes/SummaryNewAgreement';

const authLoader = async () => {
  const data = await GetAuthenticationStatus();

  if (!data.isMitIDAuthenticated) {
    const currentUrl = window.location.href;
    const returnUrl = encodeURIComponent(currentUrl);
    return redirect(`/login?returnUrl=${returnUrl}`);
  }

  //temp validation of query parameter "ano" and path. When deliveryapproval is changed to use query param instead of path. Change validation to a and b param.
  const queryParams = new URLSearchParams(window.location.search);
  const hasAnoParameter = queryParams.has('ano');
  const hasAandBParam = queryParams.has('a') && queryParams.has('b');
  const isAnonymous = hasAnoParameter || hasAandBParam;
  //____

  if (!isAnonymous && !data.isFFAuthenticated) {
    return redirect('/connect-user/:uuid');
  }
  return null; // continue if authenticated
};

function RouteErrorBoundary() {
  const { t } = useTranslation();
  const error = useRouteError();

  useEffect(() => {
    if (import.meta.env.PROD && error) {
      const exception =
        error instanceof Error
          ? error
          : new Error(
              typeof error === 'string' ? error : 'Unknown React Router error',
            );

      appInsights.trackException({
        exception,
        severityLevel: SeverityLevel.Error,
      });
    }
  }, [error]);

  return (
    <>
      <TopLineBanner
        type="danger"
        label={t('error.api.message')}
        message={t('error.api.title')}
      />
      <Header />
    </>
  );
}

export default function () {
  return (
    <RouterProvider
      future={{
        v7_startTransition: true,
      }}
      router={createBrowserRouter([
        {
          path: '/login',
          element: <LoginPage />,
          ErrorBoundary: RouteErrorBoundary,
        },
        {
          path: '/connect-user/:uuid',
          element: <LoginConnectUserPage />,
          ErrorBoundary: RouteErrorBoundary,
        },
        {
          path: '/receipt',
          element: <ReceiptPage />,
          ErrorBoundary: RouteErrorBoundary,
        },
        {
          path: '/delivery-not-found',
          element: <ReceiptDeliveryNotFoundPage />,
          ErrorBoundary: RouteErrorBoundary,
        },
        {
          path: '/new-agreement/summary',
          element: <SummaryNewAgreement />,
          ErrorBoundary: RouteErrorBoundary,
        },
        {
          id: 'new-agreement-receipt',
          path: '/new-agreement/receipt/:agreementId',
          element: <ReceiptNewAgreement />,
          ErrorBoundary: RouteErrorBoundary,
        },
        {
          path: '/',
          loader: authLoader,
          element: <Layout />,
          ErrorBoundary: RouteErrorBoundary,
          children: [
            {
              path: '',
              index: true,
              element: <Navigate to="/agreements/leasingagreements" replace />,
            },
            ...([...routes] as RouteObject[]),
            {
              path: '*', // 404
              element: <NotFoundPage />,
            },
          ],
        },
      ])}
    />
  );
}

function Layout() {
  const { t } = useTranslation();
  const revalidator = useRevalidator();
  const isMutating = useIsMutating();
  const prevMutating = useRef(isMutating);

  const { state } = useNavigation();
  const location = useLocation();

  useEffect(() => {
    if (prevMutating.current > 0 && isMutating === 0) {
      // revalidate route data when mutations are done
      if (revalidator.state === 'idle') {
        revalidator.revalidate();
      }
    }
    prevMutating.current = isMutating;
  }, [isMutating, revalidator]);

  useEffect(() => {
    let progressBarFrame: number | null = null;
    if (state === 'loading') {
      const progressBarTimer = window.setTimeout(() => {
        progressBarFrame = window.requestAnimationFrame(() => {
          NProgress.start();
        });
        document.body.classList.add('cursor-wait!');
      }, 150); // do not show progress bar if loading is fast
      return () => {
        if (NProgress.status !== null) {
          NProgress.done();
        } else if (progressBarFrame) {
          window.cancelAnimationFrame(progressBarFrame);
        } else if (progressBarTimer) {
          window.clearTimeout(progressBarTimer);
        }
        document.body.classList.remove('cursor-wait!');
      };
    } else {
      document.body.classList.remove('cursor-wait!');
    }
  }, [state]);

  const isExpiringFlow = location.pathname.startsWith(
    '/expiringagreements/flow/',
  );

  const shouldHideSideNav = isExpiringFlow;
  const isConfirmRoute = location.pathname.includes('confirm');

  return (
    <>
      <ScrollRestoration />
      <Header />
      <div className="flex h-dvh md:h-full">
        <nav
          role={'navigation'}
          className={cn(
            'fixed z-10 hidden max-h-screen w-fit overscroll-none md:h-full lg:flex lg:max-w-22 lg:justify-center lg:pt-14',
            { hidden: shouldHideSideNav },
          )}
        >
          {!shouldHideSideNav && (
            <SideNavigation
              links={[
                // TODO: mqv-ext, unsure if the user should have a home with dashboard features?
                // {
                //   icon: 'home',
                //   title: t('sideMenu.home'),
                //   to: `/`,
                // },
                {
                  icon: 'loan-alt',
                  title: t('sideMenu.ownAgreements'),
                  to: `/agreements/leasingagreements`,
                },
                {
                  icon: 'task-done',
                  title: t('sideMenu.deliveryApprovals'),
                  to: `/deliveryapprovals`,
                },
                {
                  icon: 'stopwatch',
                  title: t('sideMenu.agreementsToExpiry'),
                  to: `/expiringagreements`,
                },
                {
                  icon: 'document',
                  title: t('sideMenu.annualStatements'),
                  to: `/annualstatements`,
                },
                {
                  icon: 'plus-circle',
                  title: t('sideMenu.newAgreement'),
                  to: `/new-agreement`,
                },
              ]}
            />
          )}
        </nav>
        <div
          className={cn(
            'relative flex h-dvh w-full max-w-full shrink grow-0',
            isConfirmRoute
              ? 'pl-0'
              : shouldHideSideNav
                ? 'lg:pl-4'
                : 'lg:pl-24',
          )}
        >
          <div className="relative flex size-full flex-col justify-between pt-14">
            <main
              role="main"
              className={cn(
                'lg:border-neutral-10 lg:shadow-lg-dl-8 rounded-lg bg-white lg:border',
                isConfirmRoute
                  ? 'md800:min-w-200 md800:max-w-confirm-main m-auto w-full min-w-0'
                  : 'lg:m-4 lg:ml-0',
              )}
              tabIndex={-1}
            >
              <ErrorBoundary inline={false}>
                <Suspense fallback={<PageSpinner />}>
                  <Outlet />
                </Suspense>
              </ErrorBoundary>
            </main>
            <Footer />
          </div>
        </div>
      </div>
    </>
  );
}
